'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { Link as RACLink, type LinkProps as RACLinkProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { NavigationProps, WithRequired } from '../types.js';
import type { BaseButtonProps, TextButtonProps } from './types.js';

export interface LinkButtonProps
  extends
    Omit<BaseButtonProps, 'id'>,
    TextButtonProps,
    WithRequired<NavigationProps, 'href'>,
    Pick<RACLinkProps, 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {}

function LinkButton(
  {
    children,
    variant = 'secondary',
    tone = 'base',
    size = 'medium',
    iconStart,
    iconEnd,
    isDisabled = false,
    fullWidth = false,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: LinkButtonProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  return (
    <RACLink
      {...props}
      ref={ref}
      className={clsx('cim-button', UNSAFE_className)}
      style={UNSAFE_style}
      data-variant={variant}
      data-tone={tone}
      data-size={size}
      data-full-width={fullWidth || undefined}
      isDisabled={isDisabled}
    >
      {iconStart}
      <span className="cim-button-text">{children}</span>
      {iconEnd}
    </RACLink>
  );
}

/**
 * Displays a labelled button that allows users to navigate to another page or resource.
 *
 * See [button usage guidelines](https://ui.cimpress.io/components/button/).
 */
const _LinkButton = withStyleProps(forwardRef(LinkButton), 'LinkButton');

export { _LinkButton as LinkButton };
